import React, { useState, useRef, useEffect, useCallback } from 'react';
import { apiFetch, API_BASE } from '../apiClient';

export default function ProcessEditChat({
  processData,
  summaryData,
  onApplyChanges,
}) {
  const [editMessages, setEditMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pendingProposal, setPendingProposal] = useState(null);

  // Voice-to-Text (Web Speech API)
  const [isListening, setIsListening] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const manualEditRef = useRef(false);
  const resumeBaseRef = useRef('');
  const [hasSpeechRecognition, setHasSpeechRecognition] = useState(false);
  const recognitionRef = useRef(null);
  const ignoreSpeechRef = useRef(false);

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      const recognition = new SpeechRecognition();
      recognition.lang = 'de-DE';
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.onresult = (event) => {
        if (ignoreSpeechRef.current) return;
        if (manualEditRef.current) return;
        let transcript = '';
        for (let i = 0; i < event.results.length; i++) {
          transcript += event.results[i][0].transcript;
        }
        const base = resumeBaseRef.current;
        setInputValue(base ? base + ' ' + transcript : transcript);
      };
      recognition.onerror = () => setIsListening(false);
      recognition.onend = () => {
        setIsListening(false);
        ignoreSpeechRef.current = false;
      };
      recognitionRef.current = recognition;
      setHasSpeechRecognition(true);
    }
    return () => { if (recognitionRef.current) recognitionRef.current.abort(); };
  }, []);

  const startVoice = useCallback(() => {
    if (!recognitionRef.current) return;
    ignoreSpeechRef.current = false;
    manualEditRef.current = false;
    resumeBaseRef.current = '';
    setInputValue('');
    recognitionRef.current.start();
    setIsListening(true);
    setIsPaused(false);
  }, []);

  const pauseVoice = useCallback(() => {
    if (!recognitionRef.current) return;
    recognitionRef.current.stop();
    setIsListening(false);
    setIsPaused(true);
  }, []);

  const resumeVoice = useCallback(() => {
    if (!recognitionRef.current) return;
    ignoreSpeechRef.current = false;
    manualEditRef.current = false;
    resumeBaseRef.current = inputValue;
    recognitionRef.current.start();
    setIsListening(true);
    setIsPaused(false);
  }, [inputValue]);

  const stopVoice = useCallback(() => {
    if (!recognitionRef.current) return;
    ignoreSpeechRef.current = true;
    recognitionRef.current.stop();
    setIsListening(false);
    setIsPaused(false);
  }, []);

  const textareaRef = useRef(null);
  const chatEndRef = useRef(null);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [editMessages, pendingProposal]);

  const sendToEditChat = useCallback(async (allMessages) => {
    setIsLoading(true);
    setError(null);

    try {
      const res = await apiFetch(`${API_BASE}/api/process-edit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: allMessages,
          processData,
          summaryData,
        }),
      });

      // Guard against proxy errors or non-JSON responses
      const contentType = res.headers.get('content-type') || '';
      if (!res.ok || !contentType.includes('application/json')) {
        throw new Error('Server nicht erreichbar. Bitte versuche es erneut.');
      }

      const data = await res.json();

      if (data.error) throw new Error(data.error);

      if (data.type === 'proposal') {
        setEditMessages(prev => [
          ...prev,
          { role: 'assistant', content: data.message, isProposal: true },
        ]);
        setPendingProposal(data);
      } else if (data.type === 'clarification') {
        setEditMessages(prev => [
          ...prev,
          { role: 'assistant', content: data.message },
        ]);
      } else if (data.type === 'done') {
        setEditMessages(prev => [
          ...prev,
          { role: 'assistant', content: data.message },
        ]);
      }
    } catch (err) {
      console.error('Process edit error:', err);
      setError(err.message);
    }
    setIsLoading(false);
  }, [processData, summaryData]);

  const handleSend = useCallback(async () => {
    const text = inputValue.trim();
    if (!text || isLoading) return;

    // Stop voice recording when sending — ignore any trailing results
    if (isListening && recognitionRef.current) {
      ignoreSpeechRef.current = true;
      recognitionRef.current.stop();
      setIsListening(false);
    }

    const userMessage = { role: 'user', content: text };
    const updatedMessages = [...editMessages, userMessage];
    setEditMessages(updatedMessages);
    setInputValue('');
    setPendingProposal(null);

    await sendToEditChat(updatedMessages);
  }, [inputValue, isLoading, isListening, editMessages, sendToEditChat]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  // Apply: commit changes, clear conversation, ready for next change
  const handleApply = useCallback(() => {
    if (!pendingProposal) return;

    onApplyChanges(pendingProposal.changes, pendingProposal.updatedSummaryText);

    setEditMessages(prev => [
      ...prev,
      { role: 'system', content: 'Änderung übernommen.' },
    ]);

    setPendingProposal(null);

    setTimeout(() => {
      setEditMessages([]);
      setError(null);
    }, 2000);
  }, [pendingProposal, onApplyChanges]);

  // Reject: keep conversation going so user can re-explain
  const handleReject = useCallback(() => {
    if (!pendingProposal) return;

    const rejectMsg = { role: 'user', content: 'Nein, das passt nicht. Lass mich nochmal anders erklären.' };
    const updatedMessages = [...editMessages, rejectMsg];
    setEditMessages(updatedMessages);
    setPendingProposal(null);

    sendToEditChat(updatedMessages);
  }, [pendingProposal, editMessages, sendToEditChat]);

  return (
    <div className="process-edit-panel">
      <div className="edit-panel-header">
        <h3>Prozess anpassen</h3>
        <p className="edit-panel-hint">
          Beschreibe welche Änderungen du am Prozess vornehmen möchtest.
        </p>
      </div>

      <div className="edit-chat-messages">
        {editMessages.length === 0 && !isLoading && (
          <div className="edit-chat-welcome">
            <div className="welcome-icon">&#9998;</div>
            <p>Du kannst jetzt Änderungen am Prozess beschreiben:</p>
            <ul className="welcome-examples">
              <li>&quot;Füge einen Schritt für Qualitätsprüfung ein&quot;</li>
              <li>&quot;Benenne Schritt 3 um in ...&quot;</li>
              <li>&quot;Entferne den letzten Schritt&quot;</li>
              <li>&quot;Tausche Schritt 2 und 3&quot;</li>
            </ul>
          </div>
        )}

        {editMessages.map((msg, i) => {
          if (msg.role === 'system') {
            return (
              <div key={i} className="edit-bubble system-confirm">
                <div className="edit-bubble-content">{msg.content}</div>
              </div>
            );
          }
          return (
            <div key={i} className={`edit-bubble ${msg.role} ${msg.isProposal ? 'proposal' : ''}`}>
              <div className="edit-bubble-content">{msg.content}</div>
            </div>
          );
        })}

        {/* Proposal action buttons — only shown while proposal is pending */}
        {pendingProposal && !isLoading && (
          <div className="proposal-actions">
            <button className="btn btn-apply" onClick={handleApply}>
              Übernehmen
            </button>
            <button className="btn btn-reject" onClick={handleReject}>
              Ablehnen
            </button>
          </div>
        )}

        {isLoading && (
          <div className="edit-bubble assistant loading-bubble">
            <div className="typing-indicator">
              <span /><span /><span />
            </div>
          </div>
        )}

        <div ref={chatEndRef} />
      </div>

      {error && (
        <div className="edit-chat-error">Fehler: {error}</div>
      )}

      <div className="edit-chat-input-area">
        <textarea
          ref={textareaRef}
          className="edit-chat-textarea"
          rows={3}
          placeholder={isListening ? 'Sprich jetzt...' : 'Beschreibe deine Änderung am Prozess...'}
          value={inputValue}
          onChange={e => { setInputValue(e.target.value); manualEditRef.current = true; }}
          onKeyDown={handleKeyDown}
          disabled={isLoading}
        />
        <div className="edit-input-footer">
          {hasSpeechRecognition && (
            <div className="edit-voice-group">
              {!isListening && !isPaused && (
                <button className="btn btn-voice" onClick={startVoice} type="button" title="Spracheingabe starten">
                  🎤 Sprechen
                </button>
              )}
              {isListening && (
                <button className="btn btn-voice listening" onClick={pauseVoice} type="button" title="Aufnahme pausieren">
                  ⏸ Pausieren
                </button>
              )}
              {isPaused && (
                <button className="btn btn-voice" onClick={resumeVoice} type="button" title="Aufnahme fortsetzen">
                  ▶ Fortsetzen
                </button>
              )}
              {!isListening && <span className="voice-hint">oder einfach tippen</span>}
            </div>
          )}
          <button
            className="btn btn-edit-send"
            onClick={handleSend}
            disabled={!inputValue.trim() || isLoading}
            style={{ background: '#2E7C99', color: '#fff', borderColor: '#2E7C99' }}
          >
            Weiter
          </button>
        </div>
      </div>
    </div>
  );
}
